import clsx from "clsx";
import React, { FunctionComponent, useState } from "react";

import { useLanguage } from "../../../i18n/useLanguage";
import { useBodyClass } from "../../../util/useBodyClass";

const images = [
  "/static/img/bmw_v2/landing-page/7degrees.png",
  "/static/img/bmw_v2/landing-page/summer-tires.jpg",
  "/static/img/bmw_v2/landing-page/winter-tires.jpg",
];

const en = [
  {
    description:
      "As the main point of contact between your BMW and the road, tires play an integral role in the performance of your vehicle. Switch to BMW Approved Cold Weather Tires when the temperature drops below 7 °C, then back to BMW Summer Performance Tires when the weather warms up. That way, you can enjoy driving your BMW as it performs to its true potential.",
    tab: (
      <>
        The 7 Degree
        <br />
        Rule.
      </>
    ),
    title: "THE 7 DEGREE RULE.",
  },
  {
    description:
      "The best tire for warmer months, summer tires represent the ideal balance of comfort, acoustics, and rolling resistance. With larger tread blocks and less lamella structure, they reliably prevent hydroplaning and offer increased directional stability and steering precision even at high speeds. Not only does this make your ride safer, it also delivers superior comfort and handling.",
    tab: (
      <>
        BMW Summer
        <br />
        Tires
      </>
    ),
    title: "BMW SUMMER PERFORMANCE TIRES.",
  },
  {
    description:
      "Perfect for Canadian winter road conditions, winter tires are safer on the road at temperatures below 7°C. They are also equipped with additional rubber patterns that open when the vehicle is in motion. This creates grip edges that help ensure high traction and directional stability on snow, slush, and mud, and improves braking distance up to 10% in wet conditions and up to 50% in ice and snow.",
    tab: (
      <>
        BMW Winter
        <br />
        Tires
      </>
    ),
    title: "BMW APPROVED COLD WEATHER TIRES.",
  },
];

const fr: typeof en = [
  {
    description:
      "En tant que principal point de contact entre votre BMW et la route, les pneus jouent un rôle intégral dans les performances de votre véhicule. Passez aux pneus hiver approuvés par BMW lorsque la température descend en dessous de 7 °C, puis revenez aux pneus été performants de BMW lorsque le temps se réchauffe. Ainsi, vous pouvez profiter de la conduite de votre BMW à son plein potentiel.",
    tab: (
      <>
        La règle des
        <br />7 degrés.
      </>
    ),
    title: "LA RÈGLE DES 7 DEGRÉS.",
  },
  {
    description:
      "Le meilleur pneu pour les mois les plus chauds, les pneus d'été représentent l'équilibre idéal entre confort, acoustique et résistance au roulement. Avec des blocs de bande de roulement plus larges et une structure de lamelles réduite, ils préviennent de manière fiable l'aquaplanage et offrent une stabilité directionnelle et une précision de conduite accrues même à grande vitesse. Non seulement cela rend votre trajet plus sûr, mais cela offre également un confort et une maniabilité supérieurs.",
    tab: (
      <>
        Pneus d&apos;été
        <br />
        BMW
      </>
    ),
    title: "PNEUS DE PERFORMANCE D'ÉTÉ BMW.",
  },
  {
    description:
      "Parfaits pour les conditions routières hivernales canadiennes, les pneus hiver sont plus sûrs sur la route à des températures inférieures à 7 °C. Ils sont également équipés de motifs en caoutchouc supplémentaires qui s'ouvrent lorsque le véhicule est en mouvement. Cela crée des bords de préhension qui aident à garantir une traction élevée et une stabilité directionnelle sur la neige, la neige fondue et la boue, et améliore la distance de freinage jusqu'à 10 % dans des conditions humides et jusqu'à 50 % sur la glace et la neige.",
    tab: (
      <>
        Pneus hiver
        <br />
        BMW
      </>
    ),
    title: "PNEUS D'HIVER APPROUVÉS PAR BMW.",
  },
];

export const LandingPage: FunctionComponent = () => {
  const [language] = useLanguage();
  const t = language === "fr" ? fr : en;
  useBodyClass("landing-page");

  const [activeTab, setActiveTab] = useState(0);

  return (
    <section className="container mx-auto px-4 pb-8 md:pb-16 pt-8 md:mt-16">
      <div className="grid grid-cols-3 gap-2 mb-2 md:mb-4 lg:max-w-[50%] lg:pr-4">
        {images.map((src, i) => (
          <button
            className={clsx(
              "border-b-4 pb-2 lg:pb-5 transition-all duration-700 hover:duration-300",
              "text-slate-400 hover:text-ink",
              "text-xs md:text-base",
              "font-medium md:font-semibold",
              "hover:border-b-ink",
              i === activeTab && "border-b-ink !text-ink"
            )}
            onClick={(): void => {
              setActiveTab(i);
            }}
            key={src}
          >
            {t[i].tab}
          </button>
        ))}
      </div>
      <div className="relative overflow-clip">
        {images.map((src, i) => {
          return (
            <div
              className={clsx(
                "grid gap-4 lg:gap-6 md:grid-cols-2 transition-transform duration-1000",
                "bg-white pb-2 w-full",
                i === 0 && "z-[2]",
                i === 1 && "z-[1]",
                i === 2 && "z-[2]",
                i === activeTab && "relative translate-x-0",
                i < activeTab && "absolute top-0 -translate-x-full",
                i > activeTab && "absolute top-0 translate-x-full"
              )}
              key={src}
            >
              <img alt={t[i].title} className="object-cover w-full" src={src} />
              <div>
                <h2 className="uppercase my-4 font-light text-2xl">
                  {t[i].title}
                </h2>
                <p className="font-thin max-w-[38rem]">{t[i].description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
